import type { ChangeEvent, FC, FormEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaShareAlt as ShareIcon } from 'react-icons/fa';
import CloseIcon from '@mui/icons-material/Close';
import type { DialogProps } from '@mui/material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type {
  GridColDef,
  GridColumnHeaderParams,
  GridPaginationModel,
  GridRowSelectionModel,
  MuiEvent,
} from '@mui/x-data-grid';
import { DataGrid, GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid';

import {
  SEARCH_FORM_SETTINGS,
  USER_ADMIN_PAGE_SIZE,
} from 'src/constants/settings';
import type { BusinessUnit } from 'src/types/businessUnit';
import type { Report } from 'src/types/report';
import type { Role, SharableUser } from 'src/types/user';
import type { TemplateOrReportItem } from 'src/types/workspace';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService, { HttpServiceInstance } from 'src/services/api/http';
import {
  useFilterContext,
  useFilterState,
} from 'src/components/app/context/FilterContext';
import { mapSharableUserToProps } from 'src/components/app/context/UserContext/helper';
import { TreeFilter } from 'src/components/common/TreeFilter';
import { filterSpecs } from 'src/components/userAdmin/UserListPage/filterTree';

import ShareModalConfirmationModal from './ShareModalConfirmationModal';
import { ShareUserListFooter } from './ShareUserListFooter';

import { styles } from 'src/components/common/ReportShareModal/index.styles';

export const REPORT_SHARE_MODAL_TEST_ID = 'report-share-modal';

export interface ReportShareModalProps
  extends Omit<DialogProps, 'onSelect' | 'open'> {
  headline?: string;
  reportOrDataline: TemplateOrReportItem | (Report[] | []) | Report;
  onClose: (didShare: boolean) => void;
  isReport: boolean;
}

interface ApiSharedWithUserIdsInterface {
  data: Array<number>;
}

const ReportShareModal: FC<ReportShareModalProps> = ({
  headline = '',
  reportOrDataline,
  isReport,
  onClose,
  sx = [],
  ...props
}) => {
  const { t } = useTranslation('report');
  const theme = useTheme();
  const upSm = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
  const [confirmationModalIsOpen, setConfirmationModalIsOpen] = useState(false);
  const [isFullSelection, setIsFullSelection] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );
  const filteredSharedWithIds = useRef<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(),
  });

  // Handles the userList.
  const [getReportProUserList, setGetReportProUserList] = useState<
    SharableUser[]
  >([]);
  const [pageIndex, setPageIndex] = useState<number>(1);

  // Handles the userList search.
  const [searchResultsCount, setSearchResultsCount] = useState<number>(0);
  const [searchString, setSearchString] = useState<string>('');

  // Handles the selected users.
  const [initialSharedWithIds, setInitialSharedWithIds] =
    useState<GridRowSelectionModel>({
      type: 'include',
      ids: new Set(),
    });
  const [pagedSelectionModel, setPagedSelectionModel] =
    useState<GridRowSelectionModel>({
      type: 'include',
      ids: new Set(),
    });

  const {
    filterGroups: appliedFilterGroups,
    setFilterGroups: setAppliedFilterGroups,
  } = useFilterContext();

  const selectedFilterState = useFilterState({
    initialFilterGroups: appliedFilterGroups,
    initialFilterSpecs: filterSpecs,
    allowCombinedFilters: true,
  });

  const {
    filterGroups: selectedFilterGroups,
    setFilterGroups: setSelectedFilterGroups,
  } = selectedFilterState;

  useEffect(() => {
    setSelectedFilterGroups(appliedFilterGroups);
  }, [appliedFilterGroups]);

  const searchFilters = useMemo(
    () =>
      Object.values(selectedFilterGroups).map((selectedFilterGroups) =>
        selectedFilterGroups.map((filterValue) => filterValue.filterExpr),
      ),
    [selectedFilterGroups],
  );

  const isList = Array.isArray(reportOrDataline);
  const reportOrDatalineId = isList
    ? reportOrDataline.map((entry) => entry.id)
    : reportOrDataline.id;

  // Table structure for the DataGrid.
  const userListColumns: GridColDef[] = [
    {
      field: 'first_name',
      flex: 2,
      headerName: t('reportInfo.firstName'),
      minWidth: 100,
      sortable: false,
    },
    {
      field: 'last_name',
      flex: 2,
      headerName: t('reportInfo.lastName'),
      minWidth: 100,
      sortable: false,
    },
    {
      field: 'email',
      flex: 3,
      headerName: t('reportInfo.email'),
      minWidth: 100,
      sortable: false,
    },
    {
      field: 'role',
      flex: 2,
      headerName: t('reportInfo.role'),
      minWidth: 100,
      sortable: false,
      valueFormatter: (role: Role) => role.name || '-',
    },
    {
      field: 'first_org_unit_id',
      flex: 2,
      headerName: t('reportInfo.orgId'),
      minWidth: 100,
      sortable: false,
    },
    {
      field: 'business_unit',
      flex: 2,
      headerName: t('reportInfo.bu'),
      minWidth: 100,
      sortable: false,
      valueFormatter: (params: BusinessUnit) => params.name || '-',
    },
    {
      field: 'first_org_unit_name',
      flex: 2,
      headerName: t('reportInfo.orgName'),
      minWidth: 100,
      sortable: false,
    },
  ];

  /**
   * Handle search string change.
   */
  const handleSearchStringChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchString(event.target.value);
  };

  const applySelectedFilters = () => {
    setAppliedFilterGroups(selectedFilterGroups);
    getFilteredSharedWithIds();
    void getSharableUsers();
  };

  /**
   * Handle search submit.
   */
  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    getFilteredSharedWithIds();
    void getSharableUsers();
  };

  /**
   * Handle column header click.
   */
  const handleColumnHeaderClick = (
    params: GridColumnHeaderParams,
    event: MuiEvent<React.MouseEvent>,
  ) => {
    // Reset the full-selection mode, when clearing the checkbox in the header column.
    if (
      isFullSelection &&
      params.field === GRID_CHECKBOX_SELECTION_COL_DEF.field
    ) {
      event.preventDefault();
      setIsFullSelection(false);
      setPagedSelectionModel({ type: 'include', ids: new Set() });
    }
  };

  /**
   * Handle page change.
   * The DataGrid component uses a zero-based index, the API uses a one-based index.
   */
  const handlePageChange = ({ page }: GridPaginationModel) => {
    if (isFullSelection) {
      setPagedSelectionModel(initialSharedWithIds);
      setIsFullSelection(false);
    }

    void getSharableUsers(page + 1);
  };

  /**
   * Depending on the 'searchString' the sharable users are fetched.
   */
  const getSharableUsers = (pageIndex = 1) => {
    if (
      searchString.length >= SEARCH_FORM_SETTINGS.MINIMUM_CHARACTERS ||
      searchFilters.length > 0
    ) {
      const requestEndpoint = `${apiPaths.USER.GET_SHARABLE_USERS_QUERY_SEARCH}${searchString}`;
      setIsLoading(true);

      return HttpServiceInstance.get(requestEndpoint, {
        templateOrReportId: 'id' in reportOrDataline && reportOrDataline.id,
        templateOrReport: isReport ? 'report' : 'template',
        filter: JSON.stringify(searchFilters),
      })
        .then(({ data }) => {
          setPageIndex(pageIndex);
          setSearchResultsCount(data.count);
          let sharableUsers = [];
          if (Array.isArray(data.results)) {
            sharableUsers = data.results.map(mapSharableUserToProps);
          }
          setGetReportProUserList(sharableUsers);
        })
        .catch((error) => Promise.reject(error))
        .finally(() => setIsLoading(false));
    }

    const requestEndpoint = apiPaths.USER.GET_REPORT_USERS;
    setIsLoading(true);

    return HttpServiceInstance.get(
      requestEndpoint,
      {
        page: pageIndex,
        pageSize: USER_ADMIN_PAGE_SIZE,
        templateOrReportId: 'id' in reportOrDataline && reportOrDataline.id,
        templateOrReport: isReport ? 'report' : 'template',
      },
      true,
    )
      .then(({ data }) => {
        setPageIndex(pageIndex);
        setSearchResultsCount(data.count);
        let sharableUsers = [];
        if (Array.isArray(data.results)) {
          sharableUsers = data.results.map(mapSharableUserToProps);
        }
        setGetReportProUserList(sharableUsers);
      })
      .catch((error) => Promise.reject(error))
      .finally(() => setIsLoading(false));
  };

  /**
   * Open the Conformation Modal.
   */
  const toggleConfirmationModal = () => {
    setConfirmationModalIsOpen(!confirmationModalIsOpen);
  };

  /**
   * Return the Api-Endpoint for updating a template (Datalines), a report or a list of them.
   */
  const getApiEndpointAndParameter = (isReport: boolean, isList: boolean) => {
    if (isList) return [apiPaths.SHARING.MULTIPLE_REPORTS, 'reports'];

    return isReport
      ? [apiPaths.SHARING.REPORT, 'report']
      : [apiPaths.SHARING.TEMPLATE, 'template'];
  };

  /**
   * Share report request.
   */
  const updateSharedWithList = () => {
    const [requestEndpoint, parameter] = getApiEndpointAndParameter(
      isReport,
      isList,
    );
    setIsLoading(true);

    const userIds = isFullSelection
      ? Array.from(initialSharedWithIds.ids)
      : Array.from(pagedSelectionModel.ids);

    const payload: Record<string, any> = {
      users: userIds,
      [parameter]: reportOrDatalineId,
      ...(isFullSelection && {
        filter: JSON.stringify(searchFilters),
        search: searchString || undefined,
      }),
    };

    return HttpService.post(requestEndpoint, JSON.stringify(payload))
      .then(() => {
        toggleConfirmationModal();
        onClose(true);
      })
      .catch(() => {
        setErrorMessage(
          t('reportInfo.shareErrorMessage', {
            itemType: isReport ? t('report') : t('dataLine'),
            context: isReport ? 'report' : 'dataLine',
          }),
        );
      })
      .finally(() => setIsLoading(false));
  };

  /**
   * Fetch all SharedWithUserIds.
   */
  const getInitialSharedWithIds = () => {
    if (isList) return new Promise<Array<number>>((resolve) => resolve([]));

    const params = {
      templateOrReportId: JSON.stringify(reportOrDatalineId),
      templateOrReport: isReport ? 'report' : 'template',
    };

    return HttpService.get(apiPaths.SHARING.GET_SHARED_WITH, params, true)
      .then((response: ApiSharedWithUserIdsInterface) => {
        const { data: sharedWithUserIds } = response;

        const selectionModel = {
          type: 'include' as const,
          ids: new Set(sharedWithUserIds),
        };
        setPagedSelectionModel(selectionModel);
        setInitialSharedWithIds(selectionModel);
        return sharedWithUserIds;
      })
      .catch(() => {
        onClose(false);
      });
  };

  const getFilteredSharedWithIds = () => {
    const params = {
      templateOrReportId: JSON.stringify(reportOrDatalineId),
      templateOrReport: isReport ? 'report' : 'template',
      filter: JSON.stringify(searchFilters),
      search: searchString || undefined,
    };

    HttpService.get(apiPaths.SHARING.GET_SHARED_WITH, params, true)
      .then((response) => {
        const { data: sharedWithUserIds } = response;
        filteredSharedWithIds.current = {
          type: 'include',
          ids: new Set(sharedWithUserIds),
        };
      })
      .catch(() => {
        onClose(false);
      });
  };

  // Reset all previously selected rows across all pages and only
  // select all rows on the current page when `isFullSelection` was set to `true`.
  useEffect(() => {
    if (isFullSelection) {
      setPagedSelectionModel({
        type: 'include',
        ids: new Set(
          getReportProUserList.map((getReportProUser) => getReportProUser.id),
        ),
      });
    }
  }, [isFullSelection, getReportProUserList]);

  // Fetch all SharedWithUserIds and all users with whom sharing is allowed on mount.
  useEffect(() => {
    setIsLoading(true);

    Promise.all([getSharableUsers(), getInitialSharedWithIds()])
      .then(([_, sharedWithUserIds]) => {
        if (searchFilters && searchFilters.length) {
          getFilteredSharedWithIds();
        } else {
          filteredSharedWithIds.current = {
            type: 'include',
            ids: new Set(sharedWithUserIds || []),
          };
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  /**
   * The searchField is rendered based on the breakpoints in a different area of the dialog.
   */
  const searchArea = (
    <Box
      component="form"
      onSubmit={handleSearchSubmit}
      sx={{ width: '100%', mb: !upSm ? 2 : 0 }}
    >
      <TextField
        data-test-id="user-admin-search-list"
        id="search-input"
        placeholder={t('reportInfo.searchUser')}
        onChange={handleSearchStringChange}
        size="medium"
        fullWidth
        value={searchString}
      />
      <TreeFilter
        filterState={selectedFilterState}
        filterOptions={['businessUnit', 'role']}
        applyFilter={applySelectedFilters}
        removeFilter={() => setSelectedFilterGroups({})}
      />
    </Box>
  );

  return (
    <Dialog
      data-test-id={REPORT_SHARE_MODAL_TEST_ID}
      fullWidth
      onClose={() => onClose(false)}
      open={true}
      maxWidth={'xl'}
      sx={[...(Array.isArray(sx) ? sx : [sx])]}
      {...props}
    >
      <Box
        sx={{
          alignItems: 'start',
          display: 'flex',
          justifyContent: 'space-between',
          mt: 4,
          mx: 6,
          mb: 2,
        }}
      >
        <Box component="span" sx={{ mr: 2 }}>
          <ShareIcon style={{ display: 'block' }} size={26} />
        </Box>
        <Box component="span" sx={{ mr: 2 }}>
          <Typography>{headline}</Typography>
        </Box>

        {upSm && searchArea}

        <IconButton
          data-test-id="dialog-close-button"
          onClick={() => onClose(false)}
        >
          <CloseIcon color="primary" />
        </IconButton>
      </Box>

      <DialogContent sx={styles.dialogContent}>
        <>
          {!upSm && searchArea}
          {getReportProUserList && (
            <DataGrid
              checkboxSelection
              columns={userListColumns}
              slots={{ footer: ShareUserListFooter as any }}
              slotProps={
                {
                  footer: {
                    isFullSelection,
                    onSelectAll: () => setIsFullSelection(true),
                    rowCount: searchResultsCount,
                    rowsPerPage: USER_ADMIN_PAGE_SIZE,
                  },
                } as any
              }
              disableColumnFilter
              disableColumnMenu
              disableColumnSelector
              disableDensitySelector
              disableRowSelectionOnClick
              disableRowSelectionExcludeModel
              keepNonExistentRowsSelected
              loading={isLoading}
              onColumnHeaderClick={handleColumnHeaderClick}
              onPaginationModelChange={handlePageChange}
              onRowSelectionModelChange={(newSelectionModel) => {
                if (isFullSelection) {
                  setIsFullSelection(false);
                }

                setPagedSelectionModel(newSelectionModel);
              }}
              paginationModel={{
                page: pageIndex - 1,
                pageSize: USER_ADMIN_PAGE_SIZE,
              }}
              paginationMode="server"
              rowCount={searchResultsCount}
              rows={getReportProUserList}
              pageSizeOptions={[USER_ADMIN_PAGE_SIZE]}
              rowSelectionModel={pagedSelectionModel}
              sx={{ flex: 1 }}
            />
          )}
        </>
      </DialogContent>
      <DialogActions>
        <Box
          sx={{
            mb: 1,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Button onClick={() => toggleConfirmationModal()} variant="cta">
            {t('reportInfo.shareUpdate')}
          </Button>
        </Box>
        <ShareModalConfirmationModal
          initialSharedWithIds={initialSharedWithIds}
          filteredSharedWithIds={filteredSharedWithIds.current}
          fullSelectionCount={isFullSelection ? searchResultsCount : undefined}
          isOpen={confirmationModalIsOpen}
          isReport={isReport}
          isList={isList && reportOrDataline.length > 1}
          message={errorMessage}
          onConfirmation={updateSharedWithList}
          onClose={() => {
            toggleConfirmationModal();
          }}
          sharedWithIds={pagedSelectionModel}
        />
      </DialogActions>
    </Dialog>
  );
};

export default ReportShareModal;
